import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import useModalInactiveState from '@fabric-app/hooks/useModalInactiveState';
import { useChatbot } from '../../hooks/useChatbot.js';
import { ChatbotErrorCodes } from '@fabric/woody-client';
import React__default, { forwardRef, useState, useMemo, useRef, useEffect } from 'react';
import { mergeRefs } from 'react-merge-refs';
import { useRunOnce } from '@fabric-app/hooks/useRunOnce';
import { framerAnimationSlideUp } from '@fabric-app/ui/constants/framerAnimations';
import { Conversation } from '@fabric-app/ui/components/Conversation';
import { useQueryResourceDetail } from '../../../resources/queries/useQueryResourceDetail.js';
import { AssistantEphemeralContextSubType } from '../../types.js';
import { getResourceGenericName } from '../../../resources/utils/getResourceGenericName.js';
import { Spinner } from '@fabric-app/ui/components/Spinner';
import { P } from '@fabric-app/ui/components/Typography';
import { useActiveContextFileProcessing } from '../../hooks/useActiveContextFileProcessing.js';
import { IconNavigational24UpArrow, IconSidePanelFullScreen, IconModelProvider16Xai, IconModelProvider16Anthropic, IconModelProvider16Gemini, IconModelProvider16Openai } from '@fabric-app/icons/svg';
import { useAuthStatus, Link } from '@fabric-app/compat';
import { Button } from '@fabric-app/ui/components/Button';
import { usePostSigninRedirect } from '../../../auth/hooks/usePostSigninRedirect.js';
import { ForcedTheme } from '@fabric-app/ui/theme/ForcedTheme';
import { AssistantQuickActions } from '../AssistantQuickActions.js';
import { appRoutes } from '../../../app/routing/routes.config.js';
import { useAssistantStore, useAssistantContext } from '../../stores/assistantStore.js';
import { pick } from '@fabric-app/utils/object/manipulation';
import { shallow as shallow$1 } from '../../../../node_modules/zustand/esm/shallow.js';
import Select, { SelectChevronDown } from '@fabric-app/ui/components/Select';
import { AssistantContext } from '../AssistantContext.js';
import { useQueryChatbotModels } from '../../queries/useQueryChatbotModels.js';
import { MultiSelect } from '@fabric-app/ui/components/MultiSelect/MultiSelect';
import { useInputControls } from '@fabric-app/hooks/useInputControls';
import { DropdownMenu } from '@fabric-app/ui/components/DropdownMenu/DropdownMenu';
import { useQueryUserPreferences } from '../../../user/queries/useQueryUserPreferences.js';
import { useMutationUserPreferencesUpdate } from '../../../user/mutations/useMutationUserPreferencesUpdate.js';
import { Flex } from '@fabric-app/ui/components/Flex';
import { styled } from 'styled-components';
import { mediaMobile } from '@fabric-app/ui/constants/styled-utils';
import { useFeatureGuard_modelSwitching } from '../../../pricing/hooks/useFeatureGuard.js';

const ContextSelector = /* @__PURE__ */ forwardRef((props, ref) => {
  const { ephemeralContext, setEphemeralContext, contexts } = useChatbot();
  const authStatus = useAuthStatus();
  const disabled = contexts.length === 1;
  return /* @__PURE__ */ jsx(Select, {
    value: ephemeralContext.subType,
    onChange: (value) => {
      const nextOption = contexts.find((c) => c.subType === value);
      if (nextOption) {
        setEphemeralContext(nextOption);
      }
    },
    disabled: contexts.length === 1,
    ref,
    ...props,
    triggerProps: {
      size: "xs",
      variant: "assistant-combobox",
      disabled,
      style: {
        fontSize: 13,
        color: "var(--fabric-color-text-primary)"
      },
      ...props.triggerProps
    },
    children: contexts.filter((context) => {
      if (authStatus !== "authenticated" && context.subType === AssistantEphemeralContextSubType.Everything) {
        return false;
      }
      return true;
    }).map((context) => /* @__PURE__ */ jsx(Select.ItemOption, {
      value: context.subType,
      children: context.subType === AssistantEphemeralContextSubType.Everything ? "Everything" : `Current ${context.subType}`
    }, context.subType))
  });
});
ContextSelector.displayName = "ContextSelector";
const providerToIcon = {
  OPENAI: /* @__PURE__ */ jsx(IconModelProvider16Openai, {
    style: {
      width: 16,
      height: 16
    }
  }),
  GEMINI: /* @__PURE__ */ jsx(IconModelProvider16Gemini, {
    style: {
      width: 16,
      height: 16
    }
  }),
  ANTHROPIC: /* @__PURE__ */ jsx(IconModelProvider16Anthropic, {
    style: {
      width: 16,
      height: 16
    }
  }),
  XAI: /* @__PURE__ */ jsx(IconModelProvider16Xai, {
    style: {
      width: 16,
      height: 16
    }
  })
};
const UpgradeBoxContainer = styled.div.withConfig({
  displayName: "MessageForm__UpgradeBoxContainer",
  componentId: "sc-df81c0d-0"
})([
  `padding:5px;`
]);
const UpgradeBox = styled(Flex).attrs({
  justifyContent: "space-between",
  alignItems: "center",
  gap: "sections"
}).withConfig({
  displayName: "MessageForm__UpgradeBox",
  componentId: "sc-df81c0d-1"
})([
  `padding:0.938rem;border-radius:0.313rem;width:100%;max-width:290px;background:#9888ff;color:white;`,
  `{max-width:100%;}`
], mediaMobile);
const ModelSelector = () => {
  const { aiModel, setAiModel } = useAssistantStore((s) => pick(s, [
    "aiModel",
    "setAiModel"
  ]));
  const { value: searchValue, onChange: onSearchChange } = useInputControls("");
  const aiModelRef = useRef(aiModel);
  aiModelRef.current = aiModel;
  const { data, isLoading } = useQueryChatbotModels();
  const { data: userPreferences, isLoading: isLoadingUserPreferences } = useQueryUserPreferences();
  const { mutate: updateUserPreferences } = useMutationUserPreferencesUpdate();
  const { check: checkModelSwitching } = useFeatureGuard_modelSwitching();
  const modelSwitchingGuard = useMemo(() => checkModelSwitching(void 0), [
    checkModelSwitching
  ]);
  const options = useMemo(() => {
    if (!data || isLoading) return [];
    return data.models.map((model) => ({
      label: model.label,
      value: model.model,
      stringLabel: model.label,
      icon: providerToIcon[model.provider] ?? void 0,
      disabled: !modelSwitchingGuard.isAllowed && model.model !== data.defaultModel
    }));
  }, [
    data,
    isLoading,
    modelSwitchingGuard.isAllowed
  ]);
  useEffect(() => {
    if (!data || isLoading || isLoadingUserPreferences || !userPreferences) return;
    if (!modelSwitchingGuard.isAllowed) {
      setAiModel(data.defaultModel);
      return;
    }
    const selectedModelExists = data.models.some((model) => model.model === aiModelRef.current);
    const userSelectedModel = userPreferences.frontend.assistant?.aiModel;
    const userSelectedModelExists = data.models.some((model) => model.model === userSelectedModel);
    if (selectedModelExists) return;
    setAiModel(userSelectedModelExists && userSelectedModel ? userSelectedModel : data.defaultModel);
  }, [
    data,
    isLoading,
    isLoadingUserPreferences,
    userPreferences,
    setAiModel,
    modelSwitchingGuard.isAllowed
  ]);
  const onChange = (value) => {
    const selectedModel = data?.models.find((model) => model.model === value);
    if (!selectedModel) return;
    setAiModel(selectedModel.model);
    updateUserPreferences({
      data: {
        frontend: {
          assistant: {
            aiModel: selectedModel.model
          }
        }
      },
      showToast: false
    });
  };
  return /* @__PURE__ */ jsx(MultiSelect, {
    value: aiModel ?? "",
    onSelectItem: onChange,
    title: "Select a model",
    isLoading,
    trigger: {
      render: ({ children, ...props }) => /* @__PURE__ */ jsxs(Conversation.FormControl, {
        disabled: isLoading,
        as: DropdownMenu.Trigger,
        ...props,
        children: [
          children,
          /* @__PURE__ */ jsx(SelectChevronDown, {})
        ]
      })
    },
    options,
    filterDisplayedOption: (option) => {
      return option.stringLabel.toLowerCase().includes(searchValue.toLowerCase());
    },
    searchProps: {
      placeholder: "Search models...",
      onChange: onSearchChange,
      value: searchValue
    },
    contentProps: {
      align: "center"
    },
    closeOnChange: true,
    desktopIcons: true,
    beforeOptions: ({ onOpenChange }) => !modelSwitchingGuard.isAllowed && /* @__PURE__ */ jsx(UpgradeBoxContainer, {
      children: /* @__PURE__ */ jsxs(UpgradeBox, {
        children: [
          /* @__PURE__ */ jsx(P, {
            size: "sm",
            color: "inherit",
            children: "Unlock all models & higher limits"
          }),
          /* @__PURE__ */ jsx(Button, {
            variant: "primary-inverse",
            onClick: () => {
              modelSwitchingGuard.showUpsellBanner();
              onOpenChange(false);
            },
            children: "Upgrade"
          })
        ]
      })
    })
  });
};
const MessageForm = /* @__PURE__ */ React__default.forwardRef(({ autoFocus: _autoFocus = false, forceDark = false }, propRef) => {
  const { chatAssistantFullscreen, setChatAssistantFullscreen } = useAssistantStore((s) => pick(s, [
    "chatAssistantFullscreen",
    "setChatAssistantFullscreen",
    "reset"
  ]), shallow$1);
  const { totalPersistent } = useAssistantContext();
  const [content, setContent] = useState("");
  const authStatus = useAuthStatus();
  const { addMessage, isLoading: isLoadingAnswer, error, ephemeralContext, persistentContext, clearMessages } = useChatbot();
  const { redirectToSignin } = usePostSigninRedirect();
  const isPersistentContext = persistentContext.resourceIds.length > 0;
  const { subType, value } = ephemeralContext;
  const { resource } = useQueryResourceDetail(value, {
    placeholderData: void 0,
    enabled: !isPersistentContext && subType === AssistantEphemeralContextSubType.File
  });
  const placeholder = useMemo(() => {
    if (isPersistentContext) return "Ask AI about current selection...";
    if (resource) return `Ask AI about this ${getResourceGenericName(resource)}`;
    return "Ask your AI to find something...";
  }, [
    resource,
    isPersistentContext
  ]);
  const [textAreaRef, setTextAreaRef] = useState(null);
  const { isMobileView } = useResponsive();
  const formRef = React__default.useRef(null);
  const ref = useMemo(() => mergeRefs([
    propRef,
    setTextAreaRef
  ]), [
    propRef
  ]);
  const isContextFileProcessing = useActiveContextFileProcessing();
  const [hasShownFileProcessing, setHasShownFileProcessing] = useState(isContextFileProcessing);
  useRunOnce(() => {
    setHasShownFileProcessing(true);
  }, {
    condition: isContextFileProcessing
  });
  const autoFocus = _autoFocus && !hasShownFileProcessing;
  const handleSubmit = (e) => {
    if (isContextFileProcessing) {
      return;
    }
    if (isLoadingAnswer) return;
    e?.preventDefault();
    addMessage(content);
    setContent("");
    textAreaRef?.focus();
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };
  const isModalInactive = useModalInactiveState(formRef);
  useRunOnce(() => {
    if (!autoFocus) return;
    window.requestAnimationFrame(() => {
      textAreaRef?.focus();
    });
  }, {
    condition: Boolean(textAreaRef)
  });
  const hiddenInput = isModalInactive && isMobileView && autoFocus;
  return /* @__PURE__ */ jsx(ForcedTheme, {
    asChild: true,
    colorScheme: "dark",
    disabled: !forceDark,
    children: /* @__PURE__ */ jsxs(Conversation.InputArea, {
      children: [
        /* @__PURE__ */ jsx(AssistantQuickActions, {}),
        /* @__PURE__ */ jsx(Conversation.Form, {
          ...framerAnimationSlideUp,
          ref: formRef,
          children: authStatus !== "authenticated" && error === ChatbotErrorCodes.USAGE_LIMIT_EXCEEDED ? /* @__PURE__ */ jsxs(Fragment, {
            children: [
              /* @__PURE__ */ jsx(P, {
                size: "sm",
                style: {
                  lineHeight: "22px"
                },
                children: "Assistant usage limit reached"
              }),
              /* @__PURE__ */ jsx(Button, {
                variant: "primary-inverse",
                style: {
                  width: "100%"
                },
                onClick: () => {
                  redirectToSignin();
                },
                children: "Log in to continue using Assistant"
              })
            ]
          }) : error === ChatbotErrorCodes.USAGE_LIMIT_EXCEEDED ? /* @__PURE__ */ jsxs(Fragment, {
            children: [
              /* @__PURE__ */ jsx(P, {
                size: "sm",
                style: {
                  lineHeight: "22px"
                },
                children: "Out of thinking credits!"
              }),
              /* @__PURE__ */ jsx(Button, {
                variant: "primary-inverse",
                style: {
                  width: "100%"
                },
                href: appRoutes.settings.account,
                as: Link,
                children: "Top up credits or upgrade plan"
              })
            ]
          }) : /* @__PURE__ */ jsxs(Fragment, {
            children: [
              /* @__PURE__ */ jsxs(Conversation.FormControlGroup, {
                children: [
                  /* @__PURE__ */ jsx(ContextSelector, {}),
                  totalPersistent > 0 && /* @__PURE__ */ jsx(AssistantContext.Default, {
                    children: /* @__PURE__ */ jsxs(Conversation.FormControl, {
                      children: [
                        totalPersistent,
                        " items attached"
                      ]
                    })
                  })
                ]
              }),
              /* @__PURE__ */ jsx(ForcedTheme, {
                colorScheme: "light",
                asChild: true,
                children: /* @__PURE__ */ jsx(Conversation.FormField, {
                  children: isContextFileProcessing ? /* @__PURE__ */ jsxs(Fragment, {
                    children: [
                      /* @__PURE__ */ jsx(Spinner, {
                        size: 16
                      }),
                      /* @__PURE__ */ jsx(P, {
                        size: "xs",
                        color: "placeholder",
                        style: {
                          lineHeight: "24px"
                        },
                        children: "Processing this file, one sec..."
                      })
                    ]
                  }) : /* @__PURE__ */ jsxs(Fragment, {
                    children: [
                      !hiddenInput && /* @__PURE__ */ jsx(Conversation.FormTextArea, {
                        autoFocus,
                        placeholder,
                        ref,
                        onChange: (e) => setContent(e.target.value),
                        value: content,
                        onKeyDown: handleKeyDown
                      }),
                      /* @__PURE__ */ jsx(Conversation.FormButton, {
                        onClick: handleSubmit,
                        disabled: content === "" || isLoadingAnswer,
                        children: /* @__PURE__ */ jsx(IconNavigational24UpArrow, {})
                      })
                    ]
                  })
                })
              }),
              /* @__PURE__ */ jsxs(Conversation.FormControlGroup, {
                justifyContent: chatAssistantFullscreen && !isMobileView ? "space-between" : "flex-start",
                children: [
                  /* @__PURE__ */ jsx(Conversation.FormControl, {
                    onClick: clearMessages,
                    children: "Clear chat"
                  }),
                  !chatAssistantFullscreen && !isMobileView && /* @__PURE__ */ jsx(Conversation.FormControl, {
                    shape: "square",
                    onClick: () => setChatAssistantFullscreen(true),
                    children: /* @__PURE__ */ jsx(IconSidePanelFullScreen, {})
                  }),
                  /* @__PURE__ */ jsx(ModelSelector, {})
                ]
              })
            ]
          })
        })
      ]
    })
  });
});
MessageForm.displayName = "MessageForm";

export { MessageForm as default };
//# sourceMappingURL=MessageForm.js.map
